// EditSectionSpecs — Section 3 of editRealty.
// Substrate: design/editRealty.html lines 433-621 — `<section id="sec-specs">`.
//
// Per Property.clean() / SUBTYPE_BY_PROPERTY_TYPE (backend models),
// each property_type maps to exactly one subtype row:
//   apartment / house  → ResidentialProperty
//   land               → LandProperty
//   commercial / industrial → CommercialProperty
//
// Per R6.1 STEP R6.1.5 (edit semantics ≠ create semantics): the active
// subtype renders WIRED; the inactive subtypes are NOT rendered at all
// (matches `hidden` attr in the canonical HTML for subtypes that do not
// match the current property_type). The canonical .ap-subtype wrappers
// are preserved per modernize.css topology requirements.
//
// Backend-truth map:
//   Residential.{total_area_m2, living_area_m2, kitchen_area_m2, rooms,
//                bathrooms, floor, total_floors, year_built,
//                has_balcony, has_parking} → ResidentialPropertySerializer
//   Land.{area_m2, purpose, has_road_access, has_utilities}
//                                          → LandPropertySerializer
//   Commercial.{total_area_m2, floor, suitable_for}
//                                          → CommercialPropertySerializer
//
// RIC-3.0 — the former design-only residential placeholders (walls,
// heating, condition, furniture, residential_complex) are now backed by
// real fields on ResidentialProperty and wired live. House-only
// land_area_m2 and the commercial building-characteristic block were
// added in the same phase. ЖК (residential_complex) is apartment-only;
// land_area_m2 is house-only.

const LAND_PURPOSE_OPTIONS = [
  { value: "residential", label: "Під житлову забудову" },
  { value: "agricultural", label: "Сільськогосподарського призначення" },
  { value: "commercial", label: "Комерційного використання" },
  { value: "industrial", label: "Промислового використання" },
  { value: "recreational", label: "Рекреаційне" },
  { value: "garden", label: "Садова / дачна" },
];

const COMMERCIAL_SUITABILITY_OPTIONS = [
  { value: "office", label: "Офіс" },
  { value: "retail", label: "Торгівля / магазин" },
  { value: "warehouse", label: "Склад" },
  { value: "restaurant", label: "Ресторан / кафе" },
  { value: "hotel", label: "Готель" },
  { value: "production", label: "Виробництво" },
  { value: "mixed", label: "Змішане використання" },
];

// RIC-3.0 — building characteristic options. Values match the backend
// WallType / HeatingType / ConditionType / FurnitureType enums; labels
// mirror design/editRealty.html. Shared by residential + commercial here.
const WALL_OPTIONS = [
  { value: "brick", label: "Цегла" },
  { value: "blocks", label: "Блоки" },
  { value: "monolith", label: "Моноліт" },
  { value: "panel", label: "Панель" },
  { value: "wood", label: "Дерево" },
  { value: "other", label: "Інше" },
];

const HEATING_OPTIONS = [
  { value: "central", label: "Центральне" },
  { value: "gas", label: "Індивідуальне (газове)" },
  { value: "electric", label: "Індивідуальне (електричне)" },
  { value: "stove", label: "Індивідуальне (пічне)" },
  { value: "solar", label: "Індивідуальне (сонячні батареї)" },
  { value: "solid", label: "Індивідуальне (твердопаливне)" },
  { value: "roof_boiler", label: "Дахова котельня" },
  { value: "other", label: "Інше" },
];

const CONDITION_OPTIONS = [
  { value: "none", label: "Без ремонту" },
  { value: "raw", label: "Сирець" },
  { value: "needs_repair", label: "Потребує ремонту" },
  { value: "cosmetic", label: "Косметичний ремонт" },
  { value: "euro", label: "Євроремонт" },
  { value: "designer", label: "Дизайнерський ремонт" },
];

const FURNITURE_OPTIONS = [
  { value: "full", label: "Є все необхідне" },
  { value: "partial", label: "Частково" },
  { value: "none", label: "Немає" },
];

const subtypeForPropertyType = (propertyType) => {
  if (propertyType === "apartment" || propertyType === "house") {
    return "residential";
  }
  if (propertyType === "land") return "land";
  if (propertyType === "commercial" || propertyType === "industrial") {
    return "commercial";
  }
  return null;
};

function ResidentialFields({ propertyType, values, onChange, fieldErrors }) {
  const setField = (key, value) => onChange({ ...values, [key]: value });

  // RIC-3.0 — apartment/house divergence: ЖК is apartment-only; the land
  // plot area is house-only. Everything else is shared residential.
  const isApartment = propertyType === "apartment";
  const isHouse = propertyType === "house";

  return (
    <div className="ap-subtype" data-subtype="residential">
      <h3 className="ap-subtype__title">
        <i className="fa-solid fa-building" aria-hidden="true" />
        Житлова нерухомість
      </h3>

      <div className="ap-row ap-row--3">
        <div className="ap-field">
          <label htmlFor="res-total">
            Загальна площа, м<sup>2</sup> <span className="req">*</span>
          </label>
          <input
            id="res-total"
            name="total_area_m2"
            type="number"
            className="form-control"
            min="0"
            step="0.1"
            required
            value={values.total_area_m2 ?? ""}
            onChange={(e) => setField("total_area_m2", e.target.value)}
          />
          {fieldErrors.total_area_m2 ? (
            <small className="form-hint text-danger">
              {fieldErrors.total_area_m2}
            </small>
          ) : null}
        </div>
        <div className="ap-field">
          <label htmlFor="res-living">
            Житлова площа, м<sup>2</sup>
          </label>
          <input
            id="res-living"
            name="living_area_m2"
            type="number"
            className="form-control"
            min="0"
            step="0.1"
            value={values.living_area_m2 ?? ""}
            onChange={(e) => setField("living_area_m2", e.target.value)}
          />
          {fieldErrors.living_area_m2 ? (
            <small className="form-hint text-danger">
              {fieldErrors.living_area_m2}
            </small>
          ) : null}
        </div>
        <div className="ap-field">
          <label htmlFor="res-kitchen">
            Площа кухні, м<sup>2</sup>
          </label>
          <input
            id="res-kitchen"
            name="kitchen_area_m2"
            type="number"
            className="form-control"
            min="0"
            step="0.1"
            value={values.kitchen_area_m2 ?? ""}
            onChange={(e) => setField("kitchen_area_m2", e.target.value)}
          />
          {fieldErrors.kitchen_area_m2 ? (
            <small className="form-hint text-danger">
              {fieldErrors.kitchen_area_m2}
            </small>
          ) : null}
        </div>
      </div>

      <div className="ap-row ap-row--3">
        <div className="ap-field">
          <label htmlFor="res-rooms">Кімнат</label>
          <input
            id="res-rooms"
            name="rooms"
            type="number"
            className="form-control"
            min="0"
            step="1"
            value={values.rooms ?? ""}
            onChange={(e) => setField("rooms", e.target.value)}
          />
          {fieldErrors.rooms ? (
            <small className="form-hint text-danger">
              {fieldErrors.rooms}
            </small>
          ) : null}
        </div>
        <div className="ap-field">
          <label htmlFor="res-bathrooms">Санвузлів</label>
          <input
            id="res-bathrooms"
            name="bathrooms"
            type="number"
            className="form-control"
            min="0"
            step="1"
            value={values.bathrooms ?? ""}
            onChange={(e) => setField("bathrooms", e.target.value)}
          />
        </div>
        <div className="ap-field">
          <label htmlFor="res-year">Рік побудови</label>
          <input
            id="res-year"
            name="year_built"
            type="number"
            className="form-control"
            min="1800"
            max="2100"
            step="1"
            value={values.year_built ?? ""}
            onChange={(e) => setField("year_built", e.target.value)}
          />
        </div>
      </div>

      <div className="ap-row ap-row--2">
        <div className="ap-field">
          <label htmlFor="res-floor">Поверх</label>
          <input
            id="res-floor"
            name="floor"
            type="number"
            className="form-control"
            step="1"
            value={values.floor ?? ""}
            onChange={(e) => setField("floor", e.target.value)}
          />
          {fieldErrors.floor ? (
            <small className="form-hint text-danger">
              {fieldErrors.floor}
            </small>
          ) : null}
        </div>
        <div className="ap-field">
          <label htmlFor="res-totalfloors">Всього поверхів у будинку</label>
          <input
            id="res-totalfloors"
            name="total_floors"
            type="number"
            className="form-control"
            min="1"
            step="1"
            value={values.total_floors ?? ""}
            onChange={(e) => setField("total_floors", e.target.value)}
          />
          {fieldErrors.total_floors ? (
            <small className="form-hint text-danger">
              {fieldErrors.total_floors}
            </small>
          ) : null}
        </div>
      </div>

      {/* RIC-3.0 — house land-plot area (house-only). */}
      {isHouse ? (
        <div className="ap-row ap-row--2">
          <div className="ap-field">
            <label htmlFor="res-land-area">
              Площа ділянки, м<sup>2</sup>
            </label>
            <input
              id="res-land-area"
              name="land_area_m2"
              type="number"
              className="form-control"
              min="0"
              step="0.1"
              value={values.land_area_m2 ?? ""}
              onChange={(e) => setField("land_area_m2", e.target.value)}
            />
            {fieldErrors.land_area_m2 ? (
              <small className="form-hint text-danger">
                {fieldErrors.land_area_m2}
              </small>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* RIC-3.0 — building characteristics (apartment + house). */}
      <div className="ap-row ap-row--3">
        <div className="ap-field">
          <label htmlFor="res-walls">Стіни</label>
          <select
            id="res-walls"
            name="walls"
            className="form-select"
            value={values.walls || ""}
            onChange={(e) => setField("walls", e.target.value)}
          >
            <option value="">—</option>
            {WALL_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="ap-field">
          <label htmlFor="res-heating">Опалення</label>
          <select
            id="res-heating"
            name="heating"
            className="form-select"
            value={values.heating || ""}
            onChange={(e) => setField("heating", e.target.value)}
          >
            <option value="">—</option>
            {HEATING_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="ap-field">
          <label htmlFor="res-condition">Ремонт</label>
          <select
            id="res-condition"
            name="condition"
            className="form-select"
            value={values.condition || ""}
            onChange={(e) => setField("condition", e.target.value)}
          >
            <option value="">—</option>
            {CONDITION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="ap-row ap-row--2">
        <div className="ap-field">
          <label htmlFor="res-furniture">Меблі</label>
          <select
            id="res-furniture"
            name="furniture"
            className="form-select"
            value={values.furniture || ""}
            onChange={(e) => setField("furniture", e.target.value)}
          >
            <option value="">—</option>
            {FURNITURE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        {/* RIC-3.0 — residential complex (ЖК), apartment-only. */}
        {isApartment ? (
          <div className="ap-field">
            <label htmlFor="res-complex">Житловий комплекс</label>
            <input
              id="res-complex"
              name="residential_complex"
              type="text"
              className="form-control"
              placeholder="SKYGARDEN, Manhattan UP …"
              value={values.residential_complex ?? ""}
              onChange={(e) => setField("residential_complex", e.target.value)}
            />
            {fieldErrors.residential_complex ? (
              <small className="form-hint text-danger">
                {fieldErrors.residential_complex}
              </small>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="ap-checkbox-row">
        <label className="check-card">
          <input
            type="checkbox"
            name="has_balcony"
            checked={Boolean(values.has_balcony)}
            onChange={(e) => setField("has_balcony", e.target.checked)}
          />
          <span className="check-card__icon">
            <i className="fa-solid fa-mountain-sun" aria-hidden="true" />
          </span>
          <span className="check-card__body">
            <strong>Балкон</strong>
          </span>
        </label>
        <label className="check-card">
          <input
            type="checkbox"
            name="has_parking"
            checked={Boolean(values.has_parking)}
            onChange={(e) => setField("has_parking", e.target.checked)}
          />
          <span className="check-card__icon">
            <i className="fa-solid fa-square-parking" aria-hidden="true" />
          </span>
          <span className="check-card__body">
            <strong>Парковка</strong>
          </span>
        </label>
      </div>
    </div>
  );
}

function LandFields({ values, onChange, fieldErrors }) {
  const setField = (key, value) => onChange({ ...values, [key]: value });

  return (
    <div className="ap-subtype" data-subtype="land">
      <h3 className="ap-subtype__title">
        <i className="fa-solid fa-mountain-sun" aria-hidden="true" />
        Земельна ділянка
      </h3>
      <div className="ap-row ap-row--2">
        <div className="ap-field">
          <label htmlFor="land-area">
            Площа, м<sup>2</sup> <span className="req">*</span>
          </label>
          <input
            id="land-area"
            name="area_m2"
            type="number"
            className="form-control"
            min="0"
            step="0.1"
            required
            value={values.area_m2 ?? ""}
            onChange={(e) => setField("area_m2", e.target.value)}
          />
          {fieldErrors.area_m2 ? (
            <small className="form-hint text-danger">
              {fieldErrors.area_m2}
            </small>
          ) : null}
        </div>
        <div className="ap-field">
          <label htmlFor="land-purpose">
            Цільове призначення <span className="req">*</span>
          </label>
          <select
            id="land-purpose"
            name="purpose"
            className="form-select"
            required
            value={values.purpose || ""}
            onChange={(e) => setField("purpose", e.target.value)}
          >
            <option value="">—</option>
            {LAND_PURPOSE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {fieldErrors.purpose ? (
            <small className="form-hint text-danger">
              {fieldErrors.purpose}
            </small>
          ) : null}
        </div>
      </div>
      <div className="ap-checkbox-row">
        <label className="check-card">
          <input
            type="checkbox"
            name="has_road_access"
            checked={Boolean(values.has_road_access)}
            onChange={(e) => setField("has_road_access", e.target.checked)}
          />
          <span className="check-card__icon">
            <i className="fa-solid fa-road" aria-hidden="true" />
          </span>
          <span className="check-card__body">
            <strong>Під'їзна дорога</strong>
          </span>
        </label>
        <label className="check-card">
          <input
            type="checkbox"
            name="has_utilities"
            checked={Boolean(values.has_utilities)}
            onChange={(e) => setField("has_utilities", e.target.checked)}
          />
          <span className="check-card__icon">
            <i className="fa-solid fa-plug" aria-hidden="true" />
          </span>
          <span className="check-card__body">
            <strong>Комунікації</strong>
          </span>
        </label>
      </div>
    </div>
  );
}

function CommercialFields({ propertyType, values, onChange, fieldErrors }) {
  const setField = (key, value) => onChange({ ...values, [key]: value });

  // R6.2B — Industrial shares the CommercialProperty backend row
  // (SUBTYPE_BY_PROPERTY_TYPE in backend/app/apps/realty/models.py),
  // so the wrapper + fields + PUT target stay identical. Only the
  // section title + icon adapt so the user sees the label that
  // matches the radio they selected. data-subtype stays "commercial"
  // — matches the canonical design/editRealty.html data-attr that
  // modernize.css keys on for this block.
  const isIndustrial = propertyType === "industrial";
  const titleIcon = isIndustrial
    ? "fa-solid fa-industry"
    : "fa-solid fa-store";
  const titleLabel = isIndustrial
    ? "Промислова нерухомість"
    : "Комерційна нерухомість";

  return (
    <div className="ap-subtype" data-subtype="commercial">
      <h3 className="ap-subtype__title">
        <i className={titleIcon} aria-hidden="true" />
        {titleLabel}
      </h3>
      <div className="ap-row ap-row--3">
        <div className="ap-field">
          <label htmlFor="com-total">
            Загальна площа, м<sup>2</sup> <span className="req">*</span>
          </label>
          <input
            id="com-total"
            name="total_area_m2"
            type="number"
            className="form-control"
            min="0"
            step="0.1"
            required
            value={values.total_area_m2 ?? ""}
            onChange={(e) => setField("total_area_m2", e.target.value)}
          />
          {fieldErrors.total_area_m2 ? (
            <small className="form-hint text-danger">
              {fieldErrors.total_area_m2}
            </small>
          ) : null}
        </div>
        <div className="ap-field">
          <label htmlFor="com-floor">Поверх</label>
          <input
            id="com-floor"
            name="floor"
            type="number"
            className="form-control"
            step="1"
            value={values.floor ?? ""}
            onChange={(e) => setField("floor", e.target.value)}
          />
        </div>
        <div className="ap-field">
          <label htmlFor="com-suitable">
            Призначення <span className="req">*</span>
          </label>
          <select
            id="com-suitable"
            name="suitable_for"
            className="form-select"
            required
            value={values.suitable_for || ""}
            onChange={(e) => setField("suitable_for", e.target.value)}
          >
            <option value="">—</option>
            {COMMERCIAL_SUITABILITY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {fieldErrors.suitable_for ? (
            <small className="form-hint text-danger">
              {fieldErrors.suitable_for}
            </small>
          ) : null}
        </div>
      </div>

      {/* RIC-3.0 — building characteristics (commercial + industrial). */}
      <div className="ap-row ap-row--3">
        <div className="ap-field">
          <label htmlFor="com-walls">Стіни</label>
          <select
            id="com-walls"
            name="walls"
            className="form-select"
            value={values.walls || ""}
            onChange={(e) => setField("walls", e.target.value)}
          >
            <option value="">—</option>
            {WALL_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="ap-field">
          <label htmlFor="com-heating">Опалення</label>
          <select
            id="com-heating"
            name="heating"
            className="form-select"
            value={values.heating || ""}
            onChange={(e) => setField("heating", e.target.value)}
          >
            <option value="">—</option>
            {HEATING_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="ap-field">
          <label htmlFor="com-condition">Ремонт</label>
          <select
            id="com-condition"
            name="condition"
            className="form-select"
            value={values.condition || ""}
            onChange={(e) => setField("condition", e.target.value)}
          >
            <option value="">—</option>
            {CONDITION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="ap-row ap-row--2">
        <div className="ap-field">
          <label htmlFor="com-furniture">Меблі</label>
          <select
            id="com-furniture"
            name="furniture"
            className="form-select"
            value={values.furniture || ""}
            onChange={(e) => setField("furniture", e.target.value)}
          >
            <option value="">—</option>
            {FURNITURE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

function EditSectionSpecs({
  propertyType,
  residential,
  land,
  commercial,
  onChangeResidential,
  onChangeLand,
  onChangeCommercial,
  fieldErrors = {},
}) {
  const subtype = subtypeForPropertyType(propertyType);

  return (
    <section id="sec-specs" className="ap-pane" aria-labelledby="ap-pane-specs">
      <header className="ap-pane__head">
        <h2 id="ap-pane-specs">
          <i className="fa-solid fa-list" aria-hidden="true" />
          Характеристики
        </h2>
        <p>Поля залежать від типу нерухомості, обраного вище.</p>
      </header>

      {subtype === "residential" ? (
        <ResidentialFields
          propertyType={propertyType}
          values={residential || {}}
          onChange={onChangeResidential}
          fieldErrors={fieldErrors}
        />
      ) : null}
      {subtype === "land" ? (
        <LandFields
          values={land || {}}
          onChange={onChangeLand}
          fieldErrors={fieldErrors}
        />
      ) : null}
      {subtype === "commercial" ? (
        <CommercialFields
          propertyType={propertyType}
          values={commercial || {}}
          onChange={onChangeCommercial}
          fieldErrors={fieldErrors}
        />
      ) : null}
    </section>
  );
}

export default EditSectionSpecs;
